/**
 * Ryan O'Dowd
 * 2024-10-12
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  addTimeToStorm,
  clearStormLessons,
  setStormIsLightningOn,
  setStormIsRainOn,
  // @TODO: setStormIsThunderOn,
  setTheme,
} from '@utils/store/actions';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import ProgressIndicator from '@components/ProgressIndicator';
import Switch from '@components/common/Switch';
import Text from '@components/common/Text';
import {
  // @TODO: Platform,
  View,
} from 'react-native';
import WidgetBase from '@components/WidgetBase';
import getStyles from './styles';
import hooks from '@hooks';

const STORM_LESSONS_NEEDED = 20;
const SEVEN_MINUTES_IN_MS = 7 * 60 * 1000;

const StormForecast = (props: StormForecastProps) => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  const [_isDialogOpen, setIsDialogOpen] = useState(false);

  const stormEndTimestamp = hooks.useTypedSelector((state) => state.stormEndTimestamp);
  const stormIsLightningOn = hooks.useTypedSelector((state) => state.stormIsLightningOn);
  const stormIsRainOn = hooks.useTypedSelector((state) => state.stormIsRainOn);
  // @TODO: const stormIsThunderOn = hooks.useTypedSelector((state) => state.stormIsThunderOn);
  const stormNumLessonsCompleted = hooks.useTypedSelector((state) => state.stormNumLessonsCompleted);
  const theme = hooks.useTypedSelector((state) => state.theme);
  const selectedTheme = hooks.useTypedSelector((state) => state.selectedTheme);

  const _getSecondsUntilNow = useCallback((timestamp) => {
    if (!timestamp) {
      return 0;
    }

    return (new Date(timestamp).valueOf() - Date.now()) / 1000;
  }, []);

  const [_secondsRemaining, setSecondsRemaining] = useState(Math.max(0, _getSecondsUntilNow(stormEndTimestamp)));

  useEffect(() => {
    if (stormNumLessonsCompleted >= STORM_LESSONS_NEEDED) {
      dispatch(setTheme('storm')); // @TODO: should be a global constant
      dispatch(addTimeToStorm(SEVEN_MINUTES_IN_MS));
      setSecondsRemaining(SEVEN_MINUTES_IN_MS / 1000);
      dispatch(clearStormLessons());
    }
  }, [stormNumLessonsCompleted, dispatch]);

  const _prettyTimeDiff = (numSeconds) => {
    if (numSeconds <= 0) {
      return ''; // button text should never be null, but when we level up, the old wrinkle date_last_passed value will be displayed until the next render
    }
    const hours = Math.floor((numSeconds % 86400) / 3600);
    const minutes = Math.floor((numSeconds % 3600) / 60);
    const seconds = Math.floor((numSeconds % 60) % 60);

    return `${hours || ''}${hours ? ':' : ''}${`${minutes}`.padStart(2, '0')}:${`${seconds}`.padStart(2, '0')}`;
  };

  useEffect(() => {
    if (_secondsRemaining > 0) {
      const timer = setInterval(() => setSecondsRemaining(_getSecondsUntilNow(stormEndTimestamp)), 1000);
      return () => clearInterval(timer);
    } else if (theme === 'storm' && _secondsRemaining <= 0) {
      // storm is over
      dispatch(setTheme(selectedTheme));
    }
  }, [stormEndTimestamp, _secondsRemaining, _getSecondsUntilNow, dispatch, theme, selectedTheme]);

  const stormPrettyTimeRemaining = _prettyTimeDiff(_secondsRemaining);

  return (
    <WidgetBase.Container
      layout={props.layout}
      cellSize={props.cellSize}
    >
      <WidgetBase.Header
        title={i18n('stormForecast')}
        rightIconName='cog'
        onPressRightIcon={() => setIsDialogOpen(true)}
      />
      <WidgetBase.Content>
        <View style={styles.forecastContentWrapper}>
          {_secondsRemaining > 0 && <Text style={styles.forecastText}>{i18n('stormEndsIn', stormPrettyTimeRemaining)}</Text>}
          <View style={styles.progressWrapper}>
            <ProgressIndicator
              category={i18n('lessons')}
              completedNum={Math.min(stormNumLessonsCompleted, STORM_LESSONS_NEEDED)}
              inProgressNum={0}
              totalNum={STORM_LESSONS_NEEDED}
              showInnerText={true}
            />
          </View>
        </View>
      </WidgetBase.Content>
      <Dialog
        visible={_isDialogOpen}
        onDismiss={() => setIsDialogOpen(false)}
      >
        <Dialog.Title>{i18n('stormSettings')}</Dialog.Title>
        <Dialog.Content>
          <View style={styles.sectionWrapper}>
            <Text style={styles.sectionTitle}>{i18n('whatIsAStorm')}</Text>
            <View style={styles.srsInfoWrapper}>
              <Text style={styles.srsInfoText}>{i18n('stormDescription1')}</Text>
              <Text style={styles.srsInfoText}>{i18n('stormDescription2')}</Text>
            </View>
          </View>

          <View style={styles.sectionWrapper}>
            <Text style={styles.sectionTitle}>{i18n('effectsToggles')}</Text>
            <View style={styles.srsInfoWrapper}>
              <Text style={styles.srsInfoText}>{i18n('ifWeatherEffectsAreDistracting')}</Text>
              <View style={styles.switches}>
                {/* @TODO:
                {Platform.OS !== 'web' &&
                  <View style={styles.switchWrapper}>{/* @TODO: add sound back? add to web? /}
                    <Switch
                      isChecked={stormIsThunderOn}
                      onValueChange={(newValue) => dispatch(setStormIsThunderOn(newValue))}
                      label={i18n('thunder')}
                      labelColor={styles.colors.dialogText}
                    />
                  </View>
                }
                */}
                <View style={styles.switchWrapper}>
                  <Switch// @TODO: for some reason, these require a double tap on mobile, even though the value is updating correctly: https://github.com/callstack/react-native-paper/issues/4361
                    isChecked={stormIsLightningOn}
                    onValueChange={(newValue) => {
                      dispatch(setStormIsLightningOn(newValue));
                    }}
                    label={i18n('lightning')}
                    labelColor={styles.colors.dialogText}
                  />
                </View>
                <View style={styles.switchWrapper}>
                  <Switch
                    isChecked={stormIsRainOn}
                    onValueChange={(newValue) => {
                      dispatch(setStormIsRainOn(newValue));
                    }}
                    label={i18n('rain')}
                    labelColor={styles.colors.dialogText}
                  />
                </View>
              </View>
            </View>
          </View>
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            mode='contained'
            onPress={() => setIsDialogOpen(false)}
          >
            {i18n('close')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </WidgetBase.Container>
  );
};

class StormForecastProps {
  layout: {
    x: number,
    y: number,
    w: number,
    h: number,
  }; // @TODO: type
  cellSize: number;
};

export default StormForecast;
